// SignPage — the public, signer-facing signing surface at /sign/:uuid.
//
// The whole signing journey lives here. A signer is emailed ONE durable link
// (`/sign/:uuid`) that never dead-ends: this page fetches GET /api/sign/:uuid
// and renders the live state — the embedded DocuSeal form when it's their turn,
// or a branded status page (awaiting / signed / executed / declined / cancelled)
// otherwise — always with the document's who's-signed status and a download,
// and (wherever a download exists) the document itself inline below the status
// (components/SignInlineDocument). WHP staff viewing a lease also get the
// Review summary above the lease in every one of those states.
//
// Document-type-aware: DOC_CONTENT supplies bespoke chrome per known type
// (lease / addendum / condition report) with a generic 'document' fallback so an
// unknown type still renders a working page. No auth — the uuid is the credential.

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getSign,
  signDocumentUrl,
  SignApiError,
  type SignView,
  type SignParty,
  type SignDocumentType,
} from '@/lib/signClient';
import { DocusealEmbed } from '@/components/DocusealEmbed';
import { ReviewSummaryCard } from '@/components/ReviewSummaryCard';
import { SignDocumentBelowStatus } from '@/components/SignInlineDocument';
import { browserSignPollEnv, startSignPoll } from '@/lib/signPoll';

// ---------------------------------------------------------------------------
// Document-type content registry (the per-type "hook"; 'document' = generic).
// ---------------------------------------------------------------------------

interface DocContent {
  label: string;
  signTitle: string;
  signIntro: string;
  /** Optional guidance panel between the intro card and the embedded form
   *  (w9: the Line-1 / disregarded-entity helper). */
  helper?: { title: string; body: string };
  /**
   * Per-type override replacing BOTH generic done screens (signed AND
   * executed). The generic copy promises "we'll email you a complete copy of
   * the fully executed …" — wrong for a w9, whose single self-signer makes
   * signing completion itself, and whose signed copy we deliberately never
   * email (it carries the TIN). The signer's first name is woven in when known
   * ("Thank you, Miles — …") via the same `hi` weave as the generic screen.
   */
  completed?: {
    title: (hi: string) => string;
    body: string;
    /**
     * The body when the owner flow has a NEXT document for this signer (plan §4) —
     * the terminal wording ("That’s everything.") is wrong while something is still
     * waiting. Absent = `body` is already true either way.
     */
    bodyWithNext?: string;
  };
  /** Per-type override for the generic cancelled screen (w9: "W-9 request"). */
  cancelled?: { title: string; body: string };
  /**
   * The label as it reads mid-sentence, when lowercasing it would be wrong (a proper
   * noun — "City of Walla Walla utilities agreement"). Default: label.toLowerCase().
   */
  labelInSentence?: string;
}

/** The document's name as it reads mid-sentence ("this lease", "this City … agreement"). */
function labelInSentence(content: DocContent): string {
  return content.labelInSentence ?? content.label.toLowerCase();
}

const DOC_CONTENT: Record<SignDocumentType, DocContent> = {
  lease: {
    label: 'Lease',
    signTitle: 'Review & sign your lease',
    signIntro:
      'Please review each page of your lease below and add your signature where indicated.',
  },
  adoption_addendum: {
    label: 'Lease Addendum',
    signTitle: 'Review & sign your lease addendum',
    signIntro: 'Please review the addendum to your lease below and sign where indicated.',
  },
  condition_report: {
    label: 'Move-In Condition Report',
    signTitle: 'Review & sign your move-in condition report',
    signIntro:
      'Please review the move-in condition report for your home below and sign where indicated.',
  },
  payment_plan: {
    label: 'Payment Plan',
    signTitle: 'Review & sign your payment plan',
    signIntro:
      'Please review your payment plan below and add your signature where indicated.',
  },
  moveout_funds_direction: {
    label: 'Move-Out Funds Direction',
    signTitle: 'Review & sign your move-out funds direction',
    signIntro:
      'Please review your move-out funds direction below and add your signature where indicated.',
  },
  // manual_document: the staff-provided document name (view.documentLabel)
  // overrides this generic label at render time so the page names the actual
  // document ("Parking Addendum"); this is the fallback when no name came back.
  manual_document: {
    label: 'Document',
    signTitle: 'Review & sign your document',
    signIntro:
      'Welcome Home Properties has sent you this document to review and sign. Please look it over and add your signature where indicated.',
  },
  property_management_agreement: {
    label: 'Property Management Agreement',
    signTitle: 'Review & sign your property management agreement',
    signIntro:
      'Please review your Exclusive Property Management Agreement below and add your signature where indicated. A reference guide is included at the back to walk you through what to expect.',
  },
  // leaseholder_change: the intro must stay accurate for EVERY party — original,
  // additional, AND outgoing residents all land here (no "welcome", nothing
  // that's only true for one of them). The first clause pivots per signer via
  // leaseholderChangeIntro (role prefix); this is the neutral baseline.
  leaseholder_change: {
    label: 'Leaseholder Change Agreement',
    signTitle: 'Review & sign your leaseholder change agreement',
    signIntro:
      'It updates who holds the lease — adding and releasing residents as of its effective date. Every party signs, and it becomes binding once everyone (including Welcome Home Properties) has signed.',
  },
  // leaseholder_removal: like leaseholder_change, the intro must stay accurate for
  // EVERY party — remaining AND outgoing residents both land here (no "welcome",
  // nothing that's only true for one of them). The first clause pivots per signer
  // via leaseholderRemovalIntro (role prefix); this is the neutral baseline.
  leaseholder_removal: {
    label: 'Leaseholder Removal Agreement',
    signTitle: 'Review & sign your leaseholder removal agreement',
    signIntro:
      'It removes an outgoing resident from the lease as of its effective date, and releases them going forward once they have moved out and returned their keys. Every party signs, and it becomes binding once everyone (including Welcome Home Properties) has signed.',
  },
  // animal_addendum: the tenant whose animal was approved. The intro states the
  // standard-terms nature (true for EVERY approved-animal owner — deliberately
  // nothing else; the document carries the terms).
  animal_addendum: {
    label: 'Animal Addendum',
    signTitle: 'Review & sign your animal addendum',
    signIntro:
      'It puts the standard animal terms — the same rules every animal owner agrees to — in writing for your approved animal. Nothing else about your lease changes.',
  },
  // animal_addendum_paid (WEL-153): a CURRENT tenant who told us they are adding
  // an animal. Distinct from `animal_addendum` above, which is the fee-free
  // accommodation addendum — this one carries the animal rent and deposit, so the
  // intro says so rather than leaving a signer to find the money in the PDF. The
  // animals and the address are woven in by animalAddendumPaidIntro when the
  // server sent them; never an id.
  animal_addendum_paid: {
    label: 'Animal Addendum',
    signTitle: 'Sign your Animal Addendum',
    signIntro:
      'This adds your new animal to your lease and sets the animal rent and deposit. Review it, then sign below.',
  },
  // w9: a property owner mid-onboarding completing their Form W-9 — a SINGLE
  // self-signer who enters their taxpayer info directly on the embedded DocuSeal
  // form (this form is the only place we ever ask for it). The label stays the
  // short "W-9" so the generic templates that lowercase it ("your w-9", the
  // declined screens) still read naturally. The completed/cancelled overrides
  // exist because the generic done screens mis-describe a single-signer,
  // no-copy-emailed document. Copy: .claude/orchestrator/copy-review-package.md
  // §W7 (approved to build; deploy gated on Miles's package review).
  w9: {
    label: 'W-9',
    signTitle: 'Complete & sign your W-9',
    signIntro:
      'Under your management agreement, Welcome Home Properties collects your rental income and reports it to the IRS at year end — your Form W-9 is what lets us report it correctly, right down to the name and number the IRS expects. Enter your taxpayer information directly on the secure form below and sign it there. This form is the only place we ever ask for it — never by email.',
    // W5 guidance surfaced at the signing moment. Future: when wired to the
    // server's per-owner computeW9Guidance, show only the guidance for THIS
    // owner's applicable classifications (doctrine: no irrelevant asides).
    helper: {
      title: 'Not sure which name goes on Line 1?',
      body: 'If you own through a single-member LLC, the IRS treats it as a “disregarded entity”: Line 1 is your own legal name, Line 2 is the LLC’s name, and the number is your SSN (or your personal EIN) — not the EIN the LLC got for its bank account. Using the bank EIN is the classic mismatch that triggers IRS notices and backup withholding. Not sure? Reply to our email and a real person will help before you sign.',
    },
    // A W-9 has one signer, so signing IS completion. No emailed copy is
    // promised (we deliberately never send one), and no download is offered —
    // the server reports download.available=false for w9 (the PDF carries the
    // TIN; the staff-download guards already treat it as radioactive).
    completed: {
      title: (hi) => `Thank you${hi} — your W-9 is on file.`,
      body: 'That’s everything. We’ll use it to report your rental income correctly at year end. For your security this page doesn’t offer a download — you had the chance to save your own copy when you completed the form, and if you ever need it again, just reply to our email and a real person will help.',
      // Owner flow (plan §4): another document still waits for this owner, so the
      // terminal "That’s everything." is dropped — the rest stays word for word.
      bodyWithNext:
        'We’ll use it to report your rental income correctly at year end. For your security this page doesn’t offer a download — you had the chance to save your own copy when you completed the form, and if you ever need it again, just reply to our email and a real person will help.',
    },
    cancelled: {
      title: 'This W-9 request was cancelled',
      body: 'This W-9 request was cancelled, so there is nothing to sign here. If you were expecting to complete a W-9, reply to our email and we’ll send you a current link.',
    },
  },
  // city_utility_agreement (docs/city-utility-agreement-plan.md §5): the property
  // OWNER signing the City of Walla Walla's own one-page form. The intro says what
  // the form does in the City's terms and names the property (woven in by
  // cityUtilityIntro); the account-number sentence is dropped when the number is
  // already printed. One signer, so signing IS completion — the bespoke completed
  // screen below, with the download (the form carries no TIN). "City" is a proper
  // noun and keeps its capital mid-sentence (labelInSentence).
  city_utility_agreement: {
    label: 'City of Walla Walla utilities agreement',
    labelInSentence: 'City of Walla Walla utilities agreement',
    signTitle: 'City of Walla Walla utilities agreement',
    signIntro: cityUtilityIntro(undefined, false),
    completed: {
      title: (hi) => `Signed — thank you${hi}.`,
      body: 'We’ll send it to the City and keep a copy with the property.',
    },
    cancelled: {
      title: 'This City utilities agreement was cancelled',
      body: 'This City of Walla Walla utilities agreement was cancelled, so there is nothing to sign here. If you were expecting to sign it, reply to our email and we’ll send you a current link.',
    },
  },
  document: {
    label: 'Document',
    signTitle: 'Review & sign your document',
    signIntro: 'Please review the document below and add your signature where indicated.',
  },
};

// Copy approved by Miles 2026-09-25 — the VENDOR W-9 (owner-sync spec §6). Same
// single-signer, no-download shape as the owner w9 entry above, in the vendor's
// voice: they do work on properties we manage and we report what we pay them at
// year end. Deliberately nothing about rental income or a management agreement
// (the owner copy's two anchors) — neither is true for a vendor.
const VENDOR_W9_CONTENT: DocContent = {
  label: 'W-9',
  signTitle: 'Complete & sign your W-9',
  signIntro:
    'You do work on properties Welcome Home Properties manages, and at year end we report what we paid you to the IRS — your Form W-9 is what lets us report it correctly, right down to the name and number the IRS expects. Enter your taxpayer information directly on the secure form below and sign it there. This form is the only place we ever ask for it — never by email.',
  helper: {
    title: 'Not sure which name goes on Line 1?',
    body: 'If your business is a single-member LLC, the IRS treats it as a “disregarded entity”: Line 1 is your own legal name, Line 2 is the business name, and the number is your SSN (or your personal EIN) — not the EIN the LLC got for its bank account. Using the bank EIN is the classic mismatch that triggers IRS notices and backup withholding. Not sure? Reply to our email and a real person will help before you sign.',
  },
  completed: {
    title: (hi) => `Thank you${hi} — your W-9 is on file.`,
    body: 'That’s everything. We’ll use it to report what we pay you correctly at year end. For your security this page doesn’t offer a download — you had the chance to save your own copy when you completed the form, and if you ever need it again, just reply to our email and a real person will help.',
  },
  cancelled: {
    title: 'This W-9 request was cancelled',
    body: 'This W-9 request was cancelled, so there is nothing to sign here. If you were expecting to complete a W-9, reply to our email and we’ll send you a current link.',
  },
};

/**
 * city_utility_agreement: the intro, with the property woven in when the server sent
 * it, and the account-number sentence only when the field is actually on the form.
 */
export function cityUtilityIntro(address: string | undefined, accountNoPrinted: boolean): string {
  const where = address?.trim() ? address.trim() : 'the property';
  const account = accountNoPrinted
    ? ''
    : ' Your account number is optional — leave it blank if you don’t have it handy.';
  return (
    'The City bills the property owner for water, sewer and garbage unless the owner asks them ' +
    `to bill the manager. This one-page City form asks the City to send the bills for ${where} ` +
    `to Welcome Home Properties.${account}`
  );
}

/** The base chrome for a document type — the w9 entry pivots on who the W-9 is for. */
export function baseDocContent(
  docType: SignDocumentType,
  w9RecipientKind: SignView['w9RecipientKind'],
): DocContent {
  if (docType === 'w9' && w9RecipientKind === 'vendor') return VENDOR_W9_CONTENT;
  return DOC_CONTENT[docType] ?? DOC_CONTENT.document;
}

/** The owner-flow Continue panel (docs/city-utility-agreement-plan.md §4). */
export interface ContinuePanel {
  title: string;
  href: string;
  buttonLabel: string;
}

/**
 * The Continue panel for a signer who just finished one document of the owner flow
 * and has another waiting — or null. Only on the signed / executed screens (which
 * includes the optimistic overlay the moment the embed completes); only for a
 * server-provided SPA-relative `/sign/<uuid>` link (never an arbitrary URL). PURE.
 */
export function continuePanelFor(
  view: Pick<SignView, 'nextStep'>,
  effState: SignView['state'],
): ContinuePanel | null {
  if (effState !== 'signed' && effState !== 'executed') return null;
  const label = view.nextStep?.documentLabel?.trim();
  const href = view.nextStep?.url?.trim();
  if (!label || !href || !/^\/sign\/[A-Za-z0-9-]+$/.test(href)) return null;
  return { title: `Next: ${label}`, href, buttonLabel: 'Continue' };
}

/**
 * Fold a background refetch into the current view. While the signer's local
 * completion is still ahead of the webhook, a refetch can come back as 'ready'
 * WITHOUT a nextStep (polls skip computing it) — keep the one the page loaded with,
 * so the Continue panel never flickers away. Any other refetch replaces the view
 * as-is (a signed/executed view carries its own, fresh nextStep). PURE.
 */
export function mergeRefetchedView(prev: SignView | null, next: SignView): SignView {
  if (next.state === 'ready' && !next.nextStep && prev?.nextStep) {
    return { ...next, nextStep: prev.nextStep };
  }
  return next;
}

/**
 * leaseholder_change: the intro's FIRST clause pivots on the signer's party —
 * the group-prefixed role (OriginalResidentN / AdditionalResidentN /
 * OutgoingResidentN) is the purpose-fit hook, so each party reads what the
 * agreement does for THEM. Original residents (and the Lessor) get the neutral
 * wording; the closing sentence is identical — and true — for everyone.
 */
function leaseholderChangeIntro(signerRole: string | undefined): string {
  const firstClause = signerRole?.startsWith('OutgoingResident')
    ? 'It releases you from the lease going forward as of its effective date.'
    : signerRole?.startsWith('AdditionalResident')
      ? 'It adds you to the lease as of its effective date.'
      : 'It updates who holds the lease — adding and releasing residents as of its effective date.';
  return `${firstClause} Every party signs, and it becomes binding once everyone (including Welcome Home Properties) has signed.`;
}

/**
 * leaseholder_removal: the intro's FIRST clause pivots on the signer's party —
 * the group-prefixed role (RemainingResidentN / OutgoingResidentN) is the
 * purpose-fit hook, so each party reads what the agreement does for THEM. Only
 * the outgoing residents are being released; remaining residents (and the
 * Lessor) get the neutral wording. The closing sentence is identical — and
 * true — for everyone.
 */
function leaseholderRemovalIntro(signerRole: string | undefined): string {
  const firstClause = signerRole?.startsWith('OutgoingResident')
    ? 'It releases you from the lease going forward, as of its effective date, once you have moved out and returned your keys.'
    : 'It removes an outgoing resident from the lease as of its effective date.';
  return `${firstClause} Every party signs, and it becomes binding once everyone (including Welcome Home Properties) has signed.`;
}

/**
 * animal_addendum_paid: the intro names the animals being added and the home
 * they are joining, because both are the whole subject of the document — and a
 * household adding a second animal has to be able to tell this addendum from the
 * last one at a glance.
 *
 * Both are woven in only when the server sent them. With neither, the sentence
 * is the name-free baseline: a placeholder ("This adds {animal names}") is worse
 * than the general wording, and an id in their place is forbidden outright
 * (docs/no-ids-in-ui.md).
 */
function animalAddendumPaidIntro(
  animalNames: string | undefined,
  address: string | undefined,
): string {
  const who = animalNames?.trim() ? animalNames.trim() : 'your new animal';
  const where = address?.trim() ? ` at ${address.trim()}` : '';
  return `This adds ${who} to your lease${where} and sets the animal rent and deposit. Review it, then sign below.`;
}

/**
 * condition_report, CORRECTED copy ("Request new signatures", 2026-10-06): the
 * resident usually signed the earlier copy already, so the intro says the report
 * was corrected and what signing this copy does — generic on purpose (the
 * correction can be anything), plus staff's "What changed" note only when one
 * was written. The address is woven in when the server sent it; never an id.
 * // PENDING MILES COPY REVIEW
 */
export function conditionReportCorrectedIntro(
  address: string | undefined,
  note?: string | undefined,
): string {
  const where = address?.trim() ? ` for ${address.trim()}` : '';
  const what = note?.trim() ? ` What changed: ${note.trim()}` : '';
  return `We’ve corrected the move-in condition report${where}. Signing this corrected copy replaces the earlier one.${what}`;
}

// ---------------------------------------------------------------------------
// Brand header + section card (mirrors IntakeFormPage / the link-broker pages).
// ---------------------------------------------------------------------------

function BrandLockup({
  propertyAddress,
}: {
  propertyAddress?: string | undefined;
}): React.JSX.Element {
  return (
    <header style={{ textAlign: 'left', marginBottom: 'var(--space-7)' }}>
      <div
        style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 26,
          lineHeight: 1.2,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: 'var(--fg)',
        }}
      >
        Welcome&nbsp;Home
      </div>
      <div
        style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 13,
          lineHeight: 1.2,
          letterSpacing: '0.42em',
          textTransform: 'uppercase',
          color: 'var(--accent)',
          paddingLeft: '0.42em',
        }}
      >
        Properties
      </div>
      <div
        style={{ width: 56, height: 1, backgroundColor: 'var(--accent)', margin: 'var(--space-4) 0 0' }}
      />
      {propertyAddress ? (
        <p
          style={{
            marginTop: 'var(--space-4)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-md)',
            color: 'var(--fg-muted)',
          }}
        >
          {propertyAddress}
        </p>
      ) : null}
    </header>
  );
}

function fmtDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// ---------------------------------------------------------------------------
// Who's-signed status block. Residents are listed by name; WHP collapses into a
// single "Welcome Home Properties" party for tenant viewers, or lists each WHP
// signer individually by name for a WHP-side viewer (WEL-29). The server decides
// which shape to send (buildSignView), so this just renders whatever parties it
// receives.
// ---------------------------------------------------------------------------

function StatusList({ parties }: { parties: SignParty[] }): React.JSX.Element | null {
  if (parties.length === 0) return null;
  return (
    <div style={{ marginTop: 'var(--space-5)' }}>
      <div
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-sm)',
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--fg-muted)',
          marginBottom: 'var(--space-3)',
        }}
      >
        Signing status
      </div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column' }}>
        {parties.map((p, i) => (
          <li
            key={`${p.label}-${i}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--space-3)',
              padding: 'var(--space-3) 0',
              borderTop: i === 0 ? 'none' : '1px solid var(--border, rgba(0,0,0,0.08))',
            }}
          >
            <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <span style={{ fontWeight: 600, color: 'var(--fg)' }}>
                {p.label}
                {p.you ? (
                  <span style={{ color: 'var(--fg-muted)', fontWeight: 400 }}> (you)</span>
                ) : null}
              </span>
              {p.sublabel ? (
                <span
                  style={{
                    fontSize: 'var(--text-sm)',
                    color: 'var(--fg-muted)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {p.sublabel}
                </span>
              ) : null}
            </span>
            <span className="pill" data-tone={p.status === 'signed' ? 'success' : 'neutral'} style={{ flexShrink: 0 }}>
              <span className="dot" data-tone={p.status === 'signed' ? 'success' : 'muted'} />
              {p.status === 'signed'
                ? p.signedAt
                  ? `Signed · ${fmtDate(p.signedAt)}`
                  : 'Signed'
                : 'Awaiting'}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// The staff Review summary card lives in components/ReviewSummaryCard (shared
// with the lease builder, so the two cannot drift). Server-gated: only a WHP
// staff view of a lease carries view.reviewSummary.

function DownloadLink({ uuid, label }: { uuid: string; label: string }): React.JSX.Element {
  return (
    <div style={{ marginTop: 'var(--space-5)', textAlign: 'center' }}>
      <a
        className="btn"
        data-variant="secondary"
        href={signDocumentUrl(uuid)}
        target="_blank"
        rel="noopener noreferrer"
      >
        Download {label}
      </a>
    </div>
  );
}

/** "Next: {label}" + Continue — the owner flow's hand-off to its next document. */
function ContinuePanelCard({ panel }: { panel: ContinuePanel }): React.JSX.Element {
  return (
    <section
      className="card"
      data-testid="sign-continue"
      style={{ marginTop: 'var(--space-5)', borderLeft: '3px solid var(--accent)' }}
    >
      <h2
        style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 'var(--text-lg)',
          fontWeight: 400,
          color: 'var(--fg)',
          margin: 0,
        }}
      >
        {panel.title}
      </h2>
      <div style={{ marginTop: 'var(--space-4)' }}>
        {/* A full page load (not client-side navigation): the next document is a
            different signing link with its own state from the first byte. */}
        <a className="btn" href={panel.href}>
          {panel.buttonLabel}
        </a>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function SignPage(): React.JSX.Element {
  const { uuid = '' } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const [view, setView] = useState<SignView | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Set the moment the embed fires `completed`, before the DocuSeal webhook has
  // round-tripped to update our record — so the signer sees an instant thank-you.
  const [localSigned, setLocalSigned] = useState(false);

  useEffect(() => {
    let alive = true;
    let redirected = false;
    setLoading(true);
    setLoadError(null);
    getSign(uuid)
      .then((v) => {
        if (!alive) return;
        // Cancelled-and-replaced: the server points at the replacement lease's
        // own signing page. Navigate with REPLACE (not push) so Back doesn't
        // bounce the signer through the dead link, and keep "Loading…" up
        // until the new uuid's fetch lands — this effect re-runs on the param
        // change. Nothing is rendered for the cancelled record.
        if (v.state === 'redirect' && v.to) {
          redirected = true;
          navigate(v.to, { replace: true });
          return;
        }
        setView(v);
      })
      .catch((err) => {
        if (!alive) return;
        setLoadError(
          err instanceof SignApiError
            ? 'This signing link is no longer valid.'
            : 'Something went wrong loading your document.',
        );
      })
      .finally(() => {
        if (alive && !redirected) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [uuid, navigate]);

  // Fallback auto-advance: while the embedded form is shown (state 'ready' and
  // the signer hasn't locally completed), re-poll the server. If DocuSeal's
  // `completed` DOM event never fires (or is named differently), the webhook
  // still flips our record — and the next poll advances the page to the
  // signed/executed thank-you. The poll stops the moment state leaves 'ready'.
  // Cadence (lib/signPoll): 8s → 20s → 60s the longer the page sits unchanged,
  // back to 8s on any interaction, and NONE while the tab is hidden (one
  // catch-up poll when it comes back) — an open tab no longer polls all night.
  useEffect(() => {
    if (localSigned || view?.state !== 'ready') return;
    let alive = true;
    const stop = startSignPoll(
      () =>
        getSign(uuid, { poll: true })
          .then((v) => {
            if (alive) setView(v);
          })
          .catch(() => {
            /* transient — keep showing the form */
          }),
      browserSignPollEnv(),
    );
    return () => {
      alive = false;
      stop();
    };
  }, [uuid, view?.state, localSigned]);

  const onCompleted = useCallback(() => {
    setLocalSigned(true);
    // Refresh as the DocuSeal `form.completed` webhook lands and flips our record
    // (a few seconds), so the status block + executed state catch up.
    const refetch = () => {
      void getSign(uuid, { poll: true })
        .then((v) => setView((prev) => mergeRefetchedView(prev, v)))
        .catch(() => {
          /* keep the optimistic thank-you */
        });
    };
    refetch();
    setTimeout(refetch, 3500);
    setTimeout(refetch, 9000);
  }, [uuid]);

  const shell = (children: React.ReactNode): React.JSX.Element => (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: 'var(--bg-alt)',
        padding: 'var(--space-7) var(--space-4)',
      }}
    >
      <div style={{ maxWidth: 760, margin: '0 auto' }}>{children}</div>
    </div>
  );

  if (loading) {
    return shell(
      <>
        <BrandLockup />
        <p style={{ textAlign: 'center', color: 'var(--fg-muted)' }}>Loading…</p>
      </>,
    );
  }

  if (loadError || !view) {
    return shell(
      <>
        <BrandLockup />
        <div className="alert" data-tone="warning">
          <div className="alert-body">
            <div className="alert-title">This link is no longer available</div>
            {loadError ?? 'Please reply to the email we sent you and a real person will help.'}
          </div>
        </div>
      </>,
    );
  }

  const docType = view.documentType;
  const baseContent = baseDocContent(docType, view.w9RecipientKind);
  // For a manual_document the server passes the staff-provided name; weave it into
  // the page chrome so it names THIS document instead of a generic "Document".
  // For a leaseholder_change / leaseholder_removal the intro's first clause pivots
  // on the signer's party (view.signerRole prefix) — see the *Intro helpers.
  const content: DocContent =
    docType === 'manual_document' && view.documentLabel?.trim()
      ? {
          label: view.documentLabel.trim(),
          signTitle: `Review & sign your ${view.documentLabel.trim()}`,
          signIntro: `Welcome Home Properties has sent you this ${view.documentLabel.trim()} to review and sign. Please look it over and add your signature where indicated.`,
        }
      : docType === 'leaseholder_change'
        ? { ...baseContent, signIntro: leaseholderChangeIntro(view.signerRole) }
        : docType === 'leaseholder_removal'
          ? { ...baseContent, signIntro: leaseholderRemovalIntro(view.signerRole) }
          : docType === 'animal_addendum_paid'
            ? {
                ...baseContent,
                signIntro: animalAddendumPaidIntro(view.animalNames, view.property),
              }
            : docType === 'city_utility_agreement'
              ? {
                  ...baseContent,
                  signIntro: cityUtilityIntro(view.property, view.cityUtilityAccountNoPrinted === true),
                }
              : docType === 'condition_report' && view.correctedVersion === true
                ? {
                    ...baseContent,
                    signIntro: conditionReportCorrectedIntro(view.property, view.correctionNote),
                  }
                : baseContent;

  // Optimistic overlay: once the embed fires `completed`, mark "you" as signed
  // and treat a still-'ready' record as 'signed' until the webhook catches up.
  const effState =
    localSigned && view.state === 'ready' ? 'signed' : view.state;
  const parties: SignParty[] =
    localSigned && view.state === 'ready'
      ? view.parties.map((p) => (p.you ? { ...p, status: 'signed' as const } : p))
      : view.parties;
  // Owner flow (plan §4): another document waits for this signer → a Continue panel
  // above the status, and no "that's everything / nothing further" wording.
  const continuePanel = continuePanelFor(view, effState);

  // The Term row belongs only to documents that HAVE a term — a lease or its
  // addendum. Payment plans / move-out funds direction / condition reports never
  // show one (a payment_plan may carry a termStart for other reasons, but it must
  // not surface as a "Term:" line here).
  const showTerm = docType === 'lease' || docType === 'adoption_addendum';
  const term =
    showTerm && view.termStart && view.termEnd
      ? `${fmtDate(view.termStart)} – ${fmtDate(view.termEnd)}`
      : '';

  // --- not found ---
  if (effState === 'not_found') {
    return shell(
      <>
        <BrandLockup />
        <div className="alert" data-tone="warning">
          <div className="alert-body">
            <div className="alert-title">This link is no longer valid</div>
            The link you followed has expired or is no longer recognized. Reply to the email we sent
            you and a real person will help.
          </div>
        </div>
      </>,
    );
  }

  // --- cancelled ---
  if (effState === 'cancelled') {
    return shell(
      <>
        <BrandLockup propertyAddress={view.property} />
        <div className="alert" data-tone="danger">
          <div className="alert-body">
            <div className="alert-title">
              {content.cancelled?.title ?? 'This document was cancelled'}
            </div>
            {content.cancelled?.body ??
              `This ${labelInSentence(content)} was cancelled, so there is nothing to sign here. If you were expecting to sign, reply to our email and we will get you a current link.`}
          </div>
        </div>
      </>,
    );
  }

  // --- declined ---
  if (effState === 'declined') {
    return shell(
      <>
        <BrandLockup propertyAddress={view.property} />
        <div className="alert" data-tone="warning">
          <div className="alert-body">
            <div className="alert-title">
              {view.declinedByYou
                ? `You declined this ${labelInSentence(content)}`
                : `This ${labelInSentence(content)} was declined`}
            </div>
            {view.declinedByYou
              ? 'Our records show you declined, so no signature is needed. If that was a mistake or your plans have changed, just reply to our email and we will help.'
              : 'This document was declined and is being revised — watch for a new one. If you have questions, reply to our email and a real person will help.'}
          </div>
        </div>
      </>,
    );
  }

  // --- bespoke completed (w9): signing IS completion ---
  // A doc type with `completed` chrome has ONE signer, so the generic signed
  // (you're-done-others-pending) and executed screens both mis-describe it —
  // and the generic signed copy promises an emailed copy we deliberately never
  // send for a W-9. Both states collapse into the bespoke thank-you. No
  // download link renders: the server reports download.available=false for w9
  // (the signed PDF carries the TIN), and the completed body says so.
  if ((effState === 'executed' || effState === 'signed') && content.completed) {
    const hi =
      view.signerFirstName && view.signerFirstName !== 'there' ? `, ${view.signerFirstName}` : '';
    return shell(
      <>
        <BrandLockup propertyAddress={view.property} />
        <div className="card" data-tone="success">
          <h1
            style={{
              fontFamily: 'var(--font-serif)',
              fontSize: 28,
              fontWeight: 400,
              margin: 0,
              color: 'var(--fg)',
            }}
          >
            {content.completed.title(hi)}
          </h1>
          <p style={{ marginTop: 'var(--space-4)', color: 'var(--fg-muted)' }}>
            {continuePanel
              ? (content.completed.bodyWithNext ?? content.completed.body)
              : content.completed.body}
          </p>
        </div>
        {continuePanel ? <ContinuePanelCard panel={continuePanel} /> : null}
        <section className="card" style={{ marginTop: 'var(--space-5)' }}>
          <StatusList parties={parties} />
          {/* The City utilities agreement offers its copy; a W-9 never does (the
              server reports download.available=false for it — the TIN guard). */}
          {view.download.available ? <DownloadLink uuid={uuid} label={`the ${content.label}`} /> : null}
        </section>
        {view.download.available ? (
          <SignDocumentBelowStatus uuid={uuid} view={view} label={content.label} />
        ) : null}
      </>,
    );
  }

  // --- executed (fully signed by all parties) ---
  if (effState === 'executed') {
    return shell(
      <>
        <BrandLockup propertyAddress={view.property} />
        <div className="card" data-tone="success">
          <h1
            style={{
              fontFamily: 'var(--font-serif)',
              fontSize: 28,
              fontWeight: 400,
              margin: 0,
              color: 'var(--fg)',
            }}
          >
            Fully executed
          </h1>
          <p style={{ marginTop: 'var(--space-4)', color: 'var(--fg-muted)' }}>
            Every party has signed. Your {labelInSentence(content)} is complete — you can download
            the executed copy below for your records.
          </p>
        </div>
        {continuePanel ? <ContinuePanelCard panel={continuePanel} /> : null}
        <section className="card" style={{ marginTop: 'var(--space-5)' }}>
          <StatusList parties={parties} />
          {view.download.available ? <DownloadLink uuid={uuid} label={content.label} /> : null}
        </section>
        <SignDocumentBelowStatus uuid={uuid} view={view} label={content.label} />
      </>,
    );
  }

  // --- signed (you're done; others pending) ---
  if (effState === 'signed') {
    const hi = view.signerFirstName && view.signerFirstName !== 'there' ? `, ${view.signerFirstName}` : '';
    return shell(
      <>
        <BrandLockup propertyAddress={view.property} />
        <div className="card" data-tone="success">
          <h1
            style={{
              fontFamily: 'var(--font-serif)',
              fontSize: 28,
              fontWeight: 400,
              margin: 0,
              color: 'var(--fg)',
            }}
          >
            Thank you{hi} — your signature is recorded.
          </h1>
          <p style={{ marginTop: 'var(--space-4)', color: 'var(--fg-muted)' }}>
            {/* "Nothing further" is untrue while the owner flow has a next document. */}
            {continuePanel ? null : <>There is nothing further for you to do. </>}
            Once every party has signed, we&apos;ll email you a complete copy of the fully executed{' '}
            {labelInSentence(content)}.
          </p>
        </div>
        {continuePanel ? <ContinuePanelCard panel={continuePanel} /> : null}
        <section className="card" style={{ marginTop: 'var(--space-5)' }}>
          <StatusList parties={parties} />
          {view.download.available ? <DownloadLink uuid={uuid} label={`the ${content.label}`} /> : null}
        </section>
        <SignDocumentBelowStatus uuid={uuid} view={view} label={content.label} />
      </>,
    );
  }

  // --- awaiting (not your turn yet) ---
  if (effState === 'awaiting') {
    return shell(
      <>
        <BrandLockup propertyAddress={view.property} />
        <section className="card">
          <h2
            style={{
              fontFamily: 'var(--font-serif)',
              fontSize: 'var(--text-lg)',
              fontWeight: 400,
              color: 'var(--fg)',
              margin: 0,
            }}
          >
            Almost there — we&apos;ll email you the moment it&apos;s your turn to sign.
          </h2>
          <p style={{ marginTop: 'var(--space-3)', color: 'var(--fg-muted)' }}>
            Your {labelInSentence(content)} is moving through signing in order. As soon as the
            parties ahead of you have signed, we&apos;ll send a fresh link so you can add your
            signature. No action is needed right now.
          </p>
          <StatusList parties={parties} />
          {view.download.available ? <DownloadLink uuid={uuid} label={`the ${content.label}`} /> : null}
        </section>
        <SignDocumentBelowStatus uuid={uuid} view={view} label={content.label} />
      </>,
    );
  }

  // --- ready: it's the signer's turn → embed the DocuSeal form ---
  return shell(
    <>
      <BrandLockup propertyAddress={view.property} />
      <section className="card" style={{ marginBottom: 'var(--space-5)' }}>
        <h1
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 'var(--text-lg)',
            fontWeight: 400,
            color: 'var(--fg)',
            margin: 0,
          }}
        >
          {content.signTitle}
        </h1>
        <p style={{ marginTop: 'var(--space-3)', color: 'var(--fg-muted)' }}>{content.signIntro}</p>
        {term ? (
          <p style={{ marginTop: 'var(--space-2)', color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>
            Term: {term}
          </p>
        ) : null}
      </section>

      {/* Per-type guidance panel (w9: Line-1 / disregarded-entity helper) —
          between the intro card and the embed, visually distinct like the
          countersigner review card (accent left border). */}
      {content.helper ? (
        <section
          className="card"
          style={{ marginBottom: 'var(--space-5)', borderLeft: '3px solid var(--accent)' }}
        >
          <h2
            style={{
              fontFamily: 'var(--font-serif)',
              fontSize: 'var(--text-lg)',
              fontWeight: 400,
              color: 'var(--fg)',
              margin: 0,
            }}
          >
            {content.helper.title}
          </h2>
          <p style={{ marginTop: 'var(--space-3)', color: 'var(--fg-muted)' }}>
            {content.helper.body}
          </p>
        </section>
      ) : null}

      {view.reviewSummary ? <ReviewSummaryCard summary={view.reviewSummary} /> : null}

      <section className="card" style={{ marginBottom: 'var(--space-5)' }}>
        {view.embedSrc ? <DocusealEmbed src={view.embedSrc} onCompleted={onCompleted} /> : null}
      </section>

      <section className="card">
        <StatusList parties={parties} />
        {view.download.available ? <DownloadLink uuid={uuid} label={`the ${content.label}`} /> : null}
      </section>
    </>,
  );
}
